import { FormikValues } from "formik"
import { FormikAutoComplete, FormikMultipleAutoComplete } from "../../../../../shared/components/forms/Formik"
import { StringKeyOf } from "../../../../../lib/lang"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { FetchPriceListsResponse, PriceListType, fetchPriceLists, PriceListQuery } from "../../priceList/priceListApi"

function transformData(data: FetchPriceListsResponse) {
    return data.priceLists
}

type FormikPriceListFieldProps<Values extends FormikValues> = Readonly<{
    name: StringKeyOf<Values>
}>

export function FormikPriceListField<Values extends FormikValues>({ name }: FormikPriceListFieldProps<Values>) {
    const { merchantId } = useMerchant()

    const priceLists = useApiQuery(fetchPriceLists, {
        queryName: PriceListQuery.LIST,
        dependencies: {
            merchantId,
        },
        select: transformData,
    })

    return (
        <LocalizedStrict id="product-create-price-lists-field-label" attrs={{ label: true }}>
            <FormikAutoComplete<Values, PriceListType>
                name={name}
                label="Price list"
                options={priceLists.data ?? []}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={(option) => {
                    if (!option) return ""
                    if (option.name) return option.name
                    const found = priceLists.data?.find((pl) => pl.id === option.id)
                    return found?.name ?? ""
                }}
                fullWidth
                renderInput={() => <></>}
            />
        </LocalizedStrict>
    )
}

type FormikPriceListsFieldProps<Values extends FormikValues> = Readonly<{
    name: StringKeyOf<Values>
}>

export function FormikPriceListsField<Values extends FormikValues>({ name }: FormikPriceListsFieldProps<Values>) {
    const { merchantId } = useMerchant()

    const priceLists = useApiQuery(fetchPriceLists, {
        queryName: PriceListQuery.LIST,
        dependencies: {
            merchantId,
        },
        select: transformData,
    })

    return (
        <LocalizedStrict id="product-create-price-lists-field-label" attrs={{ label: true }}>
            <FormikMultipleAutoComplete<Values, PriceListType, PriceListType>
                name={name}
                label="Price lists"
                options={priceLists.data ?? []}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                areOptionsEqual={(x, y) => x.id === y.id}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option}
                disableCloseOnSelect
                fullWidth
            />
        </LocalizedStrict>
    )
}
